import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import {
  homeContentInnerSx,
  homeHeroGreetingSx,
  homeHeroWelcomeSx,
  providerShellHeroShellSx,
  providerShellHeroSx,
} from '@/features/home/styles/homeStyles'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useNotify } from '@/shared/toast/ToastProvider'
import { useSessionStore } from '@/stores/useSessionStore'
import { useNotificationsInfinite } from '../api/notifications-infinite-queries'
import { useBellUnreadCount } from '../api/notifications-queries'
import {
  useClearReadNotifications,
  useMarkAllNotificationsRead,
} from '../api/notifications-mutations'
import { resolveNotificationNavigatePath } from '../lib/resolve-notification-path'
import {
  formatNotificationsCardTitle,
  NOTIFICATIONS_CLEAR_READ,
  NOTIFICATIONS_LOAD_MORE_ERROR,
  NOTIFICATIONS_MARK_ALL_READ,
  NOTIFICATIONS_PAGE_LOAD_ERROR,
  NOTIFICATIONS_PAGE_SUBTITLE,
  NOTIFICATIONS_PAGE_TITLE,
  NOTIFICATIONS_READ_TAB,
  NOTIFICATIONS_RETRY,
  NOTIFICATIONS_UNREAD_TAB,
} from '../lib/notifications-copy'
import { NotificationFullList } from '../components/NotificationFullList'
import { NotificationConnectionLostBanner } from '../components/NotificationConnectionLostBanner'
import { NotificationsPageSkeleton } from '../components/NotificationsPageSkeleton'
import {
  notificationTabSx,
  notificationsCardActionsSx,
  notificationsCardHeaderSx,
  notificationsCardSx,
  notificationsCardTitleSx,
  notificationsClearReadSx,
  notificationsContentInnerSx,
  notificationsContentShellSx,
  notificationsMarkAllSx,
  notificationsPageRootSx,
  notificationsPageSubtitleSx,
  notificationsPageTitleSx,
  notificationsTabsRowSx,
} from '../styles/notificationsPageStyles'

type FilterTab = 'unread' | 'read'

function greetingFirstName(displayName: string | undefined): string {
  const part = displayName?.trim().split(/\s+/)[0]
  return part || 'there'
}

export function NotificationsPage() {
  useDocumentTitle('Notifications')
  const navigate = useNavigate()
  const notify = useNotify()
  const userName = useSessionStore((s) => s.user?.name)
  const firstName = greetingFirstName(userName)
  const { data: unreadCount = 0 } = useBellUnreadCount()
  const [activeTab, setActiveTab] = useState<FilterTab | null>(null)
  const resolvedTab: FilterTab = activeTab ?? (unreadCount > 0 ? 'unread' : 'read')
  const isReadFilter = resolvedTab === 'read'

  const listQuery = useNotificationsInfinite(isReadFilter)
  const {
    data: listData,
    isError: isListError,
    isFetchNextPageError,
    fetchNextPage,
  } = listQuery
  const { mutate: markAllRead, isPending: isMarkingAll } = useMarkAllNotificationsRead()
  const { mutate: clearRead, isPending: isClearingRead } = useClearReadNotifications()

  const items = useMemo(
    () => listData?.pages.flatMap((page) => page.items) ?? [],
    [listData?.pages],
  )

  const tabCounts = listData?.pages[0]?.meta ?? { unreadCount: 0, readCount: 0, total: 0 }
  const totalNotificationCount = tabCounts.unreadCount + tabCounts.readCount

  useEffect(() => {
    if (isListError) {
      notify.showToast(NOTIFICATIONS_PAGE_LOAD_ERROR, 'error')
    }
  }, [isListError, notify])

  useEffect(() => {
    if (!isFetchNextPageError) return
    notify.showToast(NOTIFICATIONS_LOAD_MORE_ERROR, 'error')
    const timer = globalThis.setTimeout(() => {
      Promise.resolve(fetchNextPage()).catch(() => undefined)
    }, 3000)
    return () => globalThis.clearTimeout(timer)
  }, [isFetchNextPageError, fetchNextPage, notify])

  const handleNavigate = (url: string) => {
    navigate(resolveNotificationNavigatePath(url))
  }

  const handleMarkAllRead = () => {
    markAllRead(undefined, {
      onSuccess: () => {
        setActiveTab('read')
      },
    })
  }

  const handleClearRead = () => {
    clearRead(undefined, {
      onSuccess: () => {
        if (resolvedTab === 'read') {
          Promise.resolve(listQuery.refetch()).catch(() => undefined)
        }
      },
    })
  }

  if (listQuery.isLoading) {
    return <NotificationsPageSkeleton />
  }

  return (
    <Box sx={notificationsPageRootSx} data-testid="provider-notifications-page">
      <Box sx={providerShellHeroSx}>
        <Box sx={providerShellHeroShellSx}>
          <Box sx={homeContentInnerSx}>
            <Typography component="h1" sx={homeHeroGreetingSx} data-testid="notifications-greeting">
              {`Hey, ${firstName}!`}
            </Typography>
            <Typography sx={homeHeroWelcomeSx}>Welcome to Brushfire</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={notificationsContentShellSx}>
        <Box sx={notificationsContentInnerSx}>
          <Typography component="h2" sx={notificationsPageTitleSx} data-testid="notifications-heading">
            {NOTIFICATIONS_PAGE_TITLE}
          </Typography>
          <Typography sx={notificationsPageSubtitleSx}>{NOTIFICATIONS_PAGE_SUBTITLE}</Typography>

          <NotificationConnectionLostBanner testId="provider-notifications-page-connection-lost" />

          {listQuery.isError ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ mb: 2, color: '#64748b' }}>{NOTIFICATIONS_PAGE_LOAD_ERROR}</Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  Promise.resolve(listQuery.refetch()).catch(() => undefined)
                }}
              >
                {NOTIFICATIONS_RETRY}
              </Button>
            </Box>
          ) : (
            <Box sx={notificationsCardSx}>
              <Box sx={notificationsCardHeaderSx}>
                <Typography sx={notificationsCardTitleSx} data-testid="provider-notifications-total-count">
                  {formatNotificationsCardTitle(totalNotificationCount)}
                </Typography>
                <Box sx={notificationsCardActionsSx}>
                  {tabCounts.unreadCount > 0 ? (
                    <Button
                      onClick={handleMarkAllRead}
                      disabled={isMarkingAll}
                      sx={notificationsMarkAllSx}
                      data-testid="provider-notifications-mark-all"
                    >
                      {isMarkingAll ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        NOTIFICATIONS_MARK_ALL_READ
                      )}
                    </Button>
                  ) : null}
                  {tabCounts.readCount > 0 ? (
                    <Button
                      onClick={handleClearRead}
                      disabled={isClearingRead}
                      sx={notificationsClearReadSx}
                      data-testid="provider-notifications-clear-read"
                    >
                      {isClearingRead ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        NOTIFICATIONS_CLEAR_READ
                      )}
                    </Button>
                  ) : null}
                </Box>
              </Box>

              <Box sx={notificationsTabsRowSx}>
                <Box
                  component="button"
                  type="button"
                  sx={notificationTabSx(resolvedTab === 'unread')}
                  onClick={() => setActiveTab('unread')}
                  data-testid="provider-notifications-tab-unread"
                >
                  {`${NOTIFICATIONS_UNREAD_TAB}  ${tabCounts.unreadCount}`}
                </Box>
                <Box
                  component="button"
                  type="button"
                  sx={notificationTabSx(resolvedTab === 'read')}
                  onClick={() => setActiveTab('read')}
                  data-testid="provider-notifications-tab-read"
                >
                  {`${NOTIFICATIONS_READ_TAB}  ${tabCounts.readCount}`}
                </Box>
              </Box>

              <NotificationFullList
                items={items}
                hasMore={listQuery.hasNextPage ?? false}
                isFetchingNextPage={listQuery.isFetchingNextPage}
                isFetchNextPageError={listQuery.isFetchNextPageError}
                onLoadMore={() => {
                  Promise.resolve(listQuery.fetchNextPage()).catch(() => undefined)
                }}
                onNavigate={handleNavigate}
              />
            </Box>
          )}
        </Box>
      </Box>

    </Box>
  )
}
